import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerformanceMonitor } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import Brain from './Brain';
import ResolutionEffects from './ResolutionEffects';
import VRControls from './VRControls';
import {
  BRAIN_SCALE,
  BRAIN_HEIGHT,
  BRAIN_OFFSET,
  generateParticles,
  generateSynapses,
  countsForQuality,
} from './anatomy';
import { useSimStore } from '../state/store';
import { resetViews } from './viewState';
import { audio } from '../audio/audioEngine';
import { camRig } from './camRig';

const GALLERY = new THREE.Color('#eef0f3');
const WARM = new THREE.Color('#fff0c4');
const HIJACK = new THREE.Color('#180a0e');

function FpsTracker() {
  const acc = useRef({ t: 0, f: 0 });
  useFrame((_, dt) => {
    const a = acc.current;
    a.t += dt;
    a.f += 1;
    if (a.t >= 0.5) {
      useSimStore.getState().setFps(Math.round(a.f / a.t));
      a.t = 0;
      a.f = 0;
    }
  });
  return null;
}

function EventClock() {
  useFrame(() => {
    const s = useSimStore.getState();
    if (s.event === 'none') return;
    const et = performance.now() / 1000 - s.eventStart;
    if (s.event === 'clutch') {
      // After 5s of stress, resolve on a 50/50 coin-flip
      if (et > 5) {
        if (Math.random() < s.clutchWinOdds) {
          s.triggerWin();
          try { audio.win(useSimStore.getState().streakCount >= 3); } catch (e) {}
        } else {
          s.triggerLoss();
          try { audio.loss(); } catch (e) {}
        }
      }
      return;
    }
    const dur = s.event === 'win' ? 2.9 : 1.7;
    if (et > dur) s.clearEvent();
  });
  return null;
}

// Frames all three (now larger) brains for any viewport aspect, and calibrates
// the Reset View baseline (OrbitControls.saveState).
function ResponsiveRig({ controlsRef }) {
  const { camera, size } = useThree();
  useEffect(() => {
    const aspect = Math.max(0.4, size.width / size.height);
    const vfov = (50 * Math.PI) / 180;
    const radius = 0.66 * BRAIN_SCALE;
    const halfW = BRAIN_OFFSET + radius + 0.5;
    const halfH = radius + 1.0;
    const distW = halfW / (Math.tan(vfov / 2) * aspect);
    const distH = halfH / Math.tan(vfov / 2);
    const dist = Math.max(distW, distH) + radius + 0.4;
    camRig.dist = dist;
    camRig.focusDist = (radius * 2 + 0.9) / (Math.tan(vfov / 2) * Math.min(1, aspect)) + radius;
    camera.fov = 50;
    camera.position.set(0, BRAIN_HEIGHT, dist);
    camera.lookAt(0, BRAIN_HEIGHT, 0);
    camera.updateProjectionMatrix();
    if (controlsRef.current) {
      controlsRef.current.target.set(0, BRAIN_HEIGHT, 0);
      controlsRef.current.update();
      controlsRef.current.saveState();
    }
  }, [camera, size.width, size.height, controlsRef]);
  return null;
}

// Guided-tour auto-orbit on load + smooth camera glide to/from a focused brain.
function FocusTourController({ controlsRef }) {
  const { camera } = useThree();
  const angle = useRef(0);
  const tourStart = useRef(performance.now() / 1000);
  const prevFocus = useRef(null);
  const prevTour = useRef(true);
  const transEnd = useRef(0);
  const tp = useRef(new THREE.Vector3());
  const panX = useRef(0);
  const tp2 = useRef(new THREE.Vector3());
  const SIDEBAR_PAN = -0.65; // shift triptych right of the open desktop sidebar

  useFrame((state, dt) => {
    const s = useSimStore.getState();
    const now = performance.now() / 1000;
    const focus = s.focusIndex;
    if (prevFocus.current !== null && focus === null) transEnd.current = now + 1.4; // glide back
    prevFocus.current = focus;
    // When the intro auto-orbit ends, glide the camera back to the clean front
    // gallery framing so the three brains + labels sit cleanly side by side.
    if (prevTour.current && !s.tourActive) transEnd.current = now + 1.6;
    prevTour.current = s.tourActive;

    let driving = false;
    const t = tp.current;
    if (s.tourActive) {
      angle.current += dt * 0.5;
      t.set(Math.sin(angle.current) * camRig.dist, BRAIN_HEIGHT + 0.4, Math.cos(angle.current) * camRig.dist);
      driving = true;
      if (now - tourStart.current > 8) s.setTour(false);
    } else if (s.compareMode) {
      t.set(0, BRAIN_HEIGHT, camRig.focusDist * 1.5);
      driving = true;
    } else if (focus !== null) {
      t.set(0, BRAIN_HEIGHT, camRig.focusDist);
      driving = true;
    } else if (now < transEnd.current) {
      t.set(0, BRAIN_HEIGHT, camRig.dist);
      driving = true;
    }

    // Lateral pan: when the desktop sidebar is open, shift the whole triptych
    // into the space to its right; recenter when collapsed or on mobile.
    const desktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    const wantPan = desktop && s.sidebarOpen ? SIDEBAR_PAN : 0;
    panX.current += (wantPan - panX.current) * (1 - Math.pow(0.0009, dt));
    const cx = panX.current;

    if (controlsRef.current) controlsRef.current.enabled = !driving && !s.dragging;
    if (driving) {
      // Frame-rate-independent easing so the glide converges in ~the same wall
      // time whether the device runs at 3 FPS or 120 FPS.
      const k = 1 - Math.pow(0.0009, dt);
      camera.position.lerp(tp2.current.set(t.x + cx, t.y, t.z), k);
      camera.lookAt(cx, BRAIN_HEIGHT, 0);
      if (controlsRef.current) controlsRef.current.target.set(cx, BRAIN_HEIGHT, 0);
    } else if (controlsRef.current) {
      // Free orbit: keep the pan by shifting camera + target together (no rotate).
      const dx = cx - controlsRef.current.target.x;
      if (Math.abs(dx) > 0.00001) {
        controlsRef.current.target.x = cx;
        camera.position.x += dx;
      }
    }
  });
  return null;
}

// Screen-shake to simulate an elevated heart rate during the Clutch Moment.
function ShakeController({ groupRef }) {
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const s = useSimStore.getState();
    let amp = 0;
    const lossStreak = s.streakType === 'loss' ? Math.min(1, s.streakCount / 5) : 0;
    if (s.event === 'clutch') {
      const et = performance.now() / 1000 - s.eventStart;
      amp = 0.012 + 0.03 * Math.min(1, et / 5) + lossStreak * 0.01;
    } else {
      amp = lossStreak * 0.006; // building dread tremor as losses stack
    }
    if (amp > 0) {
      g.position.set((Math.random() - 0.5) * amp, (Math.random() - 0.5) * amp, 0);
      g.rotation.z = (Math.random() - 0.5) * amp * 0.3;
    } else if (g.position.lengthSq() > 1e-6 || g.rotation.z !== 0) {
      g.position.multiplyScalar(0.8);
      g.rotation.z *= 0.8;
    }
  });
  return null;
}

function EnvironmentController() {
  const { scene } = useThree();
  const bg = useRef(new THREE.Color().copy(GALLERY));
  const lossV = useRef(0);
  const winV = useRef(0);
  const ambient = useRef();
  const hemi = useRef();
  const dir = useRef();
  const crimson = useRef();
  const gold = useRef();

  useEffect(() => {
    scene.background = bg.current;
    scene.fog = new THREE.Fog(bg.current.clone(), 11, 48);
    return () => {
      scene.background = null;
      scene.fog = null;
    };
  }, [scene]);

  useFrame((_, dt) => {
    const s = useSimStore.getState();
    const lossTarget = s.event === 'loss' ? 1 : 0;
    const winTarget = s.event === 'win' ? 1 : 0;
    lossV.current += (lossTarget - lossV.current) * Math.min(1, (lossTarget > lossV.current ? 6 : 1.1) * dt);
    winV.current += (winTarget - winV.current) * Math.min(1, (winTarget > winV.current ? 5 : 1.6) * dt);
    const l = lossV.current;
    const w = winV.current;

    const streakN = Math.min(1, s.streakCount / 5);
    const lossStreak = s.streakType === 'loss' ? streakN : 0;
    const winStreak = s.streakType === 'win' ? streakN : 0;
    const now = performance.now() / 1000;

    const c = bg.current;
    c.copy(GALLERY);
    c.lerp(WARM, w * 0.75 + winStreak * 0.12);
    c.lerp(HIJACK, Math.min(1, l + lossStreak * 0.16));
    if (scene.fog) scene.fog.color.copy(c);

    if (ambient.current) ambient.current.intensity = 0.95 * (1 - l) * (1 - lossStreak * 0.18) * (1 + w * 0.35) + 0.04;
    if (hemi.current) hemi.current.intensity = 0.85 * (1 - l);
    if (dir.current) dir.current.intensity = 0.7 * (1 - l * 0.85) + w * 0.4;
    let clutchPulse = 0;
    if (s.event === 'clutch') {
      clutchPulse = (0.5 + 0.5 * Math.sin(now * 20)) * 5;
    }
    const dread = lossStreak * 2.6 * (0.6 + 0.4 * Math.sin(now * 3));
    if (crimson.current) crimson.current.intensity = l * 8 + clutchPulse + dread;
    if (gold.current) gold.current.intensity = w * 14 + winStreak * 3;
  });

  return (
    <>
      <ambientLight ref={ambient} intensity={0.95} />
      <hemisphereLight ref={hemi} args={['#ffffff', '#c7ccd4', 0.85]} />
      <directionalLight ref={dir} position={[4, 7, 5]} intensity={0.7} color="#fff6e8" />
      <pointLight ref={crimson} position={[0, BRAIN_HEIGHT, 0]} color="#ff2a2a" intensity={0} distance={22} />
      <pointLight ref={gold} position={[0, BRAIN_HEIGHT, 0.6]} color="#ffcf5a" intensity={0} distance={26} />
    </>
  );
}

export default function Scene() {
  const setQuality = useSimStore((s) => s.setQuality);
  const quality = useSimStore((s) => s.quality);
  const dragging = useSimStore((s) => s.dragging);
  const resetNonce = useSimStore((s) => s.resetNonce);
  const inVR = useXR((s) => !!s.session);
  const controls = useRef();
  const shake = useRef();
  const { gl } = useThree();

  useEffect(() => {
    gl.localClippingEnabled = true;
  }, [gl]);

  const data = useMemo(() => {
    const count = countsForQuality(quality);
    const p = generateParticles(count);
    const synapses = generateSynapses(p.positions, quality === 'low' ? 120 : 240);
    return { ...p, synapses };
  }, [quality]);

  // Reset Views: zero brain rotations and restore the camera framing.
  useEffect(() => {
    resetViews();
    if (controls.current) controls.current.reset();
  }, [resetNonce]);

  return (
    <>
      <EnvironmentController />

      <group ref={shake}>
        {/* LEFT — permanent SIDE (sagittal) view · Matte Porcelain White */}
        <Brain
          index={0}
          position={[-BRAIN_OFFSET, BRAIN_HEIGHT, 0]}
          rotation={[0, -Math.PI / 2, 0]}
          scale={BRAIN_SCALE}
          data={data}
          label="SIDE VIEW"
          compareRole="win"
          material={{ color: '#f3f0ea', opacity: 0.42, matte: true }}
        />
        {/* CENTER — permanent CORONAL view · Crystal Clear Glass, sliced open */}
        <Brain
          index={1}
          position={[0, BRAIN_HEIGHT, 0]}
          rotation={[0, 0, 0]}
          scale={BRAIN_SCALE}
          data={data}
          label="CORONAL VIEW"
          clip
          material={{ color: '#dcefff', opacity: 0.05, matte: false }}
        />
        {/* RIGHT — permanent TOP (superior) view · Light Gray + fluorescent cyan */}
        <Brain
          index={2}
          position={[BRAIN_OFFSET, BRAIN_HEIGHT, 0]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={BRAIN_SCALE}
          data={data}
          label="TOP VIEW"
          compareRole="loss"
          material={{ color: '#c6cbd2', opacity: 0.3, matte: true, glow: '#00f0ff', glowStrength: 0.7 }}
        />
      </group>

      <ResolutionEffects />
      {inVR && <VRControls />}

      <FpsTracker />
      <EventClock />
      <ShakeController groupRef={shake} />
      <ResponsiveRig controlsRef={controls} />
      <FocusTourController controlsRef={controls} />

      <PerformanceMonitor onDecline={() => setQuality('low')} onIncline={() => setQuality('high')} />

      <OrbitControls
        ref={controls}
        makeDefault
        enabled={!dragging}
        target={[0, BRAIN_HEIGHT, 0]}
        enableRotate={false}
        enablePan={false}
        enableZoom
        enableDamping
        dampingFactor={0.08}
        minDistance={2.2}
        maxDistance={40}
      />
    </>
  );
}
